import WYSIWYG from '@components/ui/WYSIWYG'
import config from '@config/seo_meta.json'
import { Content } from '@prismicio/client'
import { SliceComponentProps } from '@prismicio/react'
import cn from 'clsx'
import throttle from 'lodash.throttle'
import Image from 'next/image'
import { RichTextBlock } from 'prismic-reactjs'
import { Fragment, useEffect, useState } from 'react'
import 'swiper/css'
import { Mousewheel } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

export type HomepageScrollSliderProps =
  SliceComponentProps<Content.HomepageScrollSliderSlice>

const HomepageScrollSlider = ({
  slice,
}: HomepageScrollSliderProps): JSX.Element => {
  const [hasScrolled, setHasScrolled] = useState(false)
  useEffect(() => {
    const handleScroll = throttle(() => {
      const offset = 0
      const { scrollTop } = document.documentElement
      const scrolled = scrollTop > offset

      if (hasScrolled !== scrolled) {
        setHasScrolled(scrolled)
      }
    }, 200)

    document.addEventListener('scroll', handleScroll)
    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [hasScrolled])

  return (
    <div
      className={cn('-mt-14.2 w-full transition-all duration-500', {
        'lg:-mt-16': hasScrolled,
        'sm:-mt-24 lg:-mt-25 xl:-mt-27': !hasScrolled,
      })}
    >
      <Swiper
        direction={'vertical'}
        slidesPerView={1}
        mousewheel={{
          sensitivity: 1,
          releaseOnEdges: true,
        }}
        touchReleaseOnEdges={true}
        modules={[Mousewheel]}
        className="mySwiper relative h-screen w-full font-roboto"
        style={{ height: 'calc(100vh - 39px)' }}
      >
        {slice?.items?.map((item, index) => {
          const {
            desktopImage,
            mobileImage,
            desktopSecondImage,
            mobileSecondImage,
            caption,
            captionColour,
            captionSecondImage,
            captionSecondImageColour,
            title,
            titleColour,
            description,
            descriptionColour,
          } = item
          const hasSecondImageDesktop =
            desktopSecondImage && desktopSecondImage.url
          return (
            <Fragment>
              <SwiperSlide key={`slide-${index}`}>
                <div className="absolute bottom-5 z-10 max-w-sm p-5 text-white lg:left-15">
                  {title && (
                    <h1
                      className="pb-4 text-5xl lg:pb-5"
                      style={{ color: titleColour! }}
                    >
                      {title}
                    </h1>
                  )}
                  {description && (
                    <div style={{ color: descriptionColour! }}>
                      <WYSIWYG content={description as RichTextBlock[]} />
                    </div>
                  )}
                </div>
                <div className="flex h-full w-full">
                  <div
                    className={cn('relative h-full', {
                      'w-full lg:w-1/2': hasSecondImageDesktop,
                      'w-full': !hasSecondImageDesktop,
                    })}
                  >
                    <Image
                      src={desktopImage?.url!}
                      alt={desktopImage?.alt || config.openGraph.images[0].alt}
                      className="object-cover"
                      fill
                    />
                  </div>
                  {hasSecondImageDesktop && (
                    <div className="relative hidden h-full w-1/2 lg:block">
                      <Image
                        src={desktopSecondImage.url}
                        alt={
                          desktopSecondImage.alt ||
                          config.openGraph.images[0].alt
                        }
                        className="object-cover"
                        fill
                      />
                    </div>
                  )}
                </div>
                <div className="lg:hidden">
                  {mobileImage?.url ? (
                    <Image
                      src={mobileImage.url}
                      alt={mobileImage.alt || config.openGraph.images[0].alt}
                      className="object-cover"
                      fill
                    />
                  ) : (
                    <Image
                      src={desktopImage?.url!}
                      alt={desktopImage?.alt || config.openGraph.images[0].alt}
                      className="object-cover"
                      fill
                    />
                  )}
                </div>
                <div
                  className="absolute right-15 bottom-5 z-20 items-end gap-x-6 text-white lg:bottom-15 lg:flex"
                  style={{ color: captionColour! }}
                >
                  {caption && <span>{caption}</span>}
                  <div className="hidden lg:block">
                    <span className="font-medium">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    /{String(slice.items.length).padStart(2, '0')}
                  </div>
                </div>
              </SwiperSlide>
              {/* Additional Mobile Slide for Second Image if it exists */}
              {mobileSecondImage && mobileSecondImage.url && (
                <SwiperSlide
                  key={`mobile-second-${index}`}
                  className="relative h-full w-full lg:!hidden"
                >
                  <Image
                    src={mobileSecondImage.url}
                    alt={
                      mobileSecondImage.alt || config.openGraph.images[0].alt
                    }
                    className="object-cover"
                    fill
                  />
                  {/* Mobile Caption for second image */}
                  {captionSecondImage && (
                    <div
                      className="absolute right-15 bottom-15 text-white"
                      style={{ color: captionSecondImageColour! }}
                    >
                      {captionSecondImage}
                    </div>
                  )}
                </SwiperSlide>
              )}
            </Fragment>
          )
        })}
      </Swiper>
    </div>
  )
}

export default HomepageScrollSlider
